import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import TermsConditions from './TermsConditions';
import { 
  User, 
  Bell, 
  Shield, 
  HelpCircle, 
  ChevronRight,
  Eye,
  Cookie,
  FileText,
  Users,
  Mail,
  Phone,
  MessageSquare,
  LucideIcon,
  BarChart3,
  Home,
  Zap,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import useThemeStore from '../store/themeStore';
import { ThemeProps } from '../utils/theme';
import backgroundPattern from '../images/vesta/vesta-property-pattern-01-teal-bg.svg';
import useTokenStore from '../store/store';
import ConfirmationModal from '../components/ConfirmationModal';
import { 
  fetchUserSettings, 
  updateNotificationSettings, 
  updatePrivacySettings 
} from '../utils/user';

// Types
interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  themeColor?: string;
  loading?: boolean;
}

interface SettingsItemProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  rightElement?: React.ReactNode;
  onClick?: () => void;
  showChevron?: boolean;
}

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  type: 'warning' | 'info' | 'success';
}

// Toggle Switch Component
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ 
  enabled, 
  onChange, 
  disabled = false, 
  themeColor = '#3b82f6',
  loading = false 
}) => {
  return (
    <button
      onClick={() => !disabled && !loading && onChange(!enabled)}
      disabled={disabled || loading}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        disabled || loading
          ? 'bg-gray-200 cursor-not-allowed' 
          : enabled 
            ? 'hover:shadow-md' 
            : 'hover:bg-gray-300'
      }`}
      style={{
        backgroundColor: enabled ? themeColor : disabled || loading ? '#e5e7eb' : '#d1d5db'
      }}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-200 shadow-sm flex items-center justify-center ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      >
        {loading && (
          <Loader2 className="w-2 h-2 animate-spin text-gray-500" />
        )}
      </span>
    </button>
  );
};

// Settings Item Component - Updated with better spacing
const SettingsItem: React.FC<SettingsItemProps> = ({ 
  icon: Icon, 
  title, 
  description, 
  rightElement, 
  onClick, 
  showChevron = false 
}) => {
  return (
    <div 
      className={`flex items-center p-4 bg-white rounded-lg border border-gray-200 transition-all duration-200 ${
        onClick 
          ? 'cursor-pointer hover:bg-gray-50 hover:shadow-md hover:border-gray-300 hover:scale-[1.02]' 
          : ''
      }`}
      onClick={onClick}
    >
      {/* Left side - Icon and text with more spacing */}
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        <div className="flex-shrink-0 ml-2">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-500">{description}</p>
          )}
        </div>
      </div>
      
      {/* Right side - Toggle/Chevron with controlled spacing */}
      <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
        {rightElement}
        {showChevron && <ChevronRight className="w-4 h-4 text-gray-400" />}
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const getToken = useTokenStore((state) => state.token);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('account');

  // Privacy & Legal Settings State
  const [privacySettings, setPrivacySettings] = useState({
    analytics: true,
    marketing: false,
    functional: true,
    essential: true // Always enabled
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    push: false,
    sms: false,
    marketing: true,
    savedSearchAlerts: true
  });

  // Loading states for individual settings
  const [loadingStates, setLoadingStates] = useState({
    notifications: false,
    privacy: false
  });

  // Settings loading state
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Modal State
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning'
  });

  // Terms Modal State
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  // Fetch user settings on component mount
  useEffect(() => {
    const loadUserSettings = async () => {
      if (!getToken) {
        setSettingsLoading(false);
        return;
      }

      try {
        setSettingsLoading(true);
        const settings = await fetchUserSettings(getToken);
        
        if (settings) {
          setNotificationSettings({
            ...settings.notificationSettings,
            // Absent on accounts predating property alerts — enabled by default
            savedSearchAlerts: settings.notificationSettings.savedSearchAlerts ?? true
          });
          setPrivacySettings(settings.privacySettings);
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadUserSettings();
  }, [getToken]);

  // Handle privacy setting changes
  // In your Settings.tsx file, update the handlePrivacyChange function:

const handlePrivacyChange = async (setting: string, newValue: boolean) => {
  if (setting === 'essential') return; // Essential cookies cannot be disabled

  setModal({
    isOpen: true,
    title: `${newValue ? 'Enable' : 'Disable'} ${setting.charAt(0).toUpperCase() + setting.slice(1)} Cookies`,
    message: newValue 
      ? `You are about to enable ${setting} cookies. This will allow us to enhance your experience on our platform. Please click confirm to proceed.`
      : `You are about to disable ${setting} cookies. This may limit some functionality of our platform. Please click confirm to proceed.`,
    onConfirm: async () => {
      try {
        setLoadingStates(prev => ({ ...prev, privacy: true }));
        
        const updatedSettings = {
          ...privacySettings,
          [setting]: newValue,
          // ADD THESE NEW REQUIRED FIELDS:
          version: '2025.01', // Get this from your PrivacyPolicy component or environment
          method: 'settings' as const // Always 'settings' when changed from Settings page
        };
        
        const result = await updatePrivacySettings(getToken || '', updatedSettings);
        
        if (result.status === 200) {
          setPrivacySettings(updatedSettings);
          setModal(prev => ({ ...prev, isOpen: false }));
        } else {
          console.error('Failed to update privacy settings:', result.data?.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error updating privacy settings:', error);
      } finally {
        setLoadingStates(prev => ({ ...prev, privacy: false }));
      }
    },
    type: newValue ? 'info' : 'warning'
  });
};

  // Handle notification setting changes
  const handleNotificationChange = async (setting: string, newValue: boolean) => {
    // Property alerts get bespoke copy: the generic template would render
    // "SavedSearchAlerts Notifications", and disabling deserves a real warning
    // because it silences the alert emails the user explicitly set up.
    const isPropertyAlerts = setting === 'savedSearchAlerts';
    const title = isPropertyAlerts
      ? `${newValue ? 'Enable' : 'Disable'} Property Alerts`
      : `${newValue ? 'Enable' : 'Disable'} ${setting.charAt(0).toUpperCase() + setting.slice(1)} Notifications`;
    const message = isPropertyAlerts
      ? newValue
        ? 'You are about to enable property alert emails for your saved searches. Please click confirm to proceed.'
        : 'You will no longer receive property alert emails for your saved filters. Please click confirm to proceed.'
      : newValue
        ? `You are about to enable ${setting} notifications. Please click confirm to proceed.`
        : `You are about to disable ${setting} notifications. Please click confirm to proceed.`;
    setModal({
      isOpen: true,
      title,
      message,
      onConfirm: async () => {
        try {
          setLoadingStates(prev => ({ ...prev, notifications: true }));
          
          const updatedSettings = {
            ...notificationSettings,
            [setting]: newValue
          };
          
          const result = await updateNotificationSettings(getToken || '', updatedSettings);
          
          if (result.status === 200) {
            setNotificationSettings(updatedSettings);
            setModal(prev => ({ ...prev, isOpen: false }));
          } else {
            console.error('Failed to update notification settings:', result.message);
            // You might want to show an error message to the user here
          }
        } catch (error) {
          console.error('Error updating notification settings:', error);
          // You might want to show an error message to the user here
        } finally {
          setLoadingStates(prev => ({ ...prev, notifications: false }));
        }
      },
      type: isPropertyAlerts && !newValue ? 'warning' : 'info'
    });
  };

  const tabs = [
    { id: 'account', name: 'Account', icon: User },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'privacy', name: 'Privacy & Legal', icon: Shield },
    { id: 'support', name: 'Support & Help', icon: HelpCircle }
  ];

  const faqData = [
    {
      question: "How do I find suitable investment properties?",
      answer: "Our platform specialises in sourcing high-yield buy-to-let properties across the UK. We provide detailed property analysis, rental yield calculations, and area insights to help you make informed investment decisions."
    },
    {
      question: "What services do you offer for property investors?",
      answer: "We offer comprehensive property investment services including property sourcing, due diligence, property management, tenant finding, rent collection, maintenance coordination, and portfolio growth strategies."
    },
    {
      question: "How do you calculate rental yields?",
      answer: "We calculate both gross and net rental yields. Gross yield is (Annual Rental Income ÷ Property Price) × 100. Net yield accounts for expenses like management fees, maintenance, insurance, and void periods."
    },
    {
      question: "What areas do you cover?",
      answer: "We focus on high-growth areas across England with strong rental demand, good transport links, and potential for capital appreciation. Our research team identifies emerging opportunities in both established and up-and-coming locations."
    },
    {
      question: "How does the property management service work?",
      answer: "Our full property management service includes tenant sourcing and vetting, rent collection, property maintenance, regular inspections, handling tenant issues, and providing monthly statements to keep you informed about your investment."
    },
    {
      question: "What are your fees and charges?",
      answer: "Our fee structure is transparent with no hidden costs. Property sourcing fees, management fees, and any additional services are clearly outlined in your service agreement. Contact us for a detailed breakdown based on your requirements."
    }
  ];

  const renderTabContent = () => {
    if (settingsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: getThemes?.color1 }} />
          <span className="ml-2 text-gray-600">Loading settings...</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-4">
            <SettingsItem
              icon={User}
              title="My Profile"
              description="Update your personal details and contact information"
              showChevron={true}
              onClick={() => navigate('/profile')}
            />
            <SettingsItem
              icon={Eye}
              title="Change Password"
              description="Update your account password"
              showChevron={true}
              onClick={() => navigate('/profile?open=password')}
            />
            <SettingsItem
              icon={AlertTriangle}
              title="Delete Account"
              description="Permanently delete your account and all data"
              showChevron={true}
              onClick={() => navigate('/profile?open=deletion')}
            />
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4">
            <SettingsItem
              icon={Mail}
              title="Email Notifications"
              description="Receive updates about your properties via email"
              rightElement={
                <ToggleSwitch
                  enabled={notificationSettings.email}
                  onChange={(value: boolean) => handleNotificationChange('email', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.notifications}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Bell}
              title="Push Notifications"
              description="Get instant notifications on your device"
              rightElement={
                <ToggleSwitch
                  enabled={notificationSettings.push}
                  onChange={(value: boolean) => handleNotificationChange('push', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.notifications}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Phone}
              title="SMS Notifications"
              description="Receive important alerts via text message"
              rightElement={
                <ToggleSwitch
                  enabled={notificationSettings.sms}
                  onChange={(value: boolean) => handleNotificationChange('sms', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.notifications}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Users}
              title="Marketing Communications"
              description="Receive updates about new properties and services"
              rightElement={
                <ToggleSwitch
                  enabled={notificationSettings.marketing}
                  onChange={(value: boolean) => handleNotificationChange('marketing', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.notifications}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Zap}
              title="Property Alerts"
              description="Personalised property alert emails for your saved searches"
              rightElement={
                <ToggleSwitch
                  enabled={notificationSettings.savedSearchAlerts}
                  onChange={(value: boolean) => handleNotificationChange('savedSearchAlerts', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.notifications}
                />
              }
              onClick={() => {}}
            />
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-4">
            <SettingsItem
              icon={Cookie}
              title="Essential Cookies"
              description="Required for basic site functionality (always enabled)"
              rightElement={
                <ToggleSwitch
                  enabled={privacySettings.essential}
                  onChange={() => {}}
                  disabled={true}
                  themeColor={getThemes?.color1}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Cookie}
              title="Analytics Cookies"
              description="Help us improve our service by analyzing site usage"
              rightElement={
                <ToggleSwitch
                  enabled={privacySettings.analytics}
                  onChange={(value: boolean) => handlePrivacyChange('analytics', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.privacy}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Cookie}
              title="Marketing Cookies"
              description="Used to show you relevant property recommendations"
              rightElement={
                <ToggleSwitch
                  enabled={privacySettings.marketing}
                  onChange={(value: boolean) => handlePrivacyChange('marketing', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.privacy}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={Cookie}
              title="Functional Cookies"
              description="Remember your preferences and settings"
              rightElement={
                <ToggleSwitch
                  enabled={privacySettings.functional}
                  onChange={(value: boolean) => handlePrivacyChange('functional', value)}
                  themeColor={getThemes?.color1}
                  loading={loadingStates.privacy}
                />
              }
              onClick={() => {}}
            />
            <SettingsItem
              icon={FileText}
              title="Privacy Policy"
              description="Read our privacy policy and data handling practices"
              showChevron={true}
              onClick={() => window.open('/privacy-policy', '_blank')}
            />
            <SettingsItem
              icon={Cookie}
              title="Cookie Settings"
              description="Manage your cookie preferences and consent choices"
              showChevron={true}
              onClick={() => {
                // Trigger the cookie consent preferences modal
                const cookieButton = document.querySelector('[data-cc="show-preferencesModal"]');
                if (cookieButton instanceof HTMLElement) {
                  cookieButton.click();
                }
              }}
            />
            <SettingsItem
              icon={FileText}
              title="Terms & Conditions"
              description="View the terms and conditions of our service"
              showChevron={true}
              onClick={() => setIsTermsModalOpen(true)}
            />
          </div>
        );

      case 'support':
        return (
          <div className="space-y-6">
            {/* FAQ Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Frequently Asked Questions</h3>
              <div className="space-y-3">
                {faqData.map((faq, index) => (
                  <details key={index} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <summary className="cursor-pointer font-medium text-gray-900 hover:text-blue-600 transition-colors">
                      {faq.question}
                    </summary>
                    <div className="mt-3 text-gray-600 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>

            {/* Contact Options */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Contact & Resources</h3>
              <SettingsItem
                icon={MessageSquare}
                title="Contact Support"
                description="Get help from our customer support team"
                showChevron={true}
                onClick={() => {
                  // Trigger the same modal as the "Speak To Our Team" button in header
                  const speakToTeamButton = document.querySelector('[data-hs-overlay="#hs-large-modal"]');
                  if (speakToTeamButton instanceof HTMLElement) {
                    speakToTeamButton.click();
                  }
                }}
              />
              <SettingsItem
                icon={FileText}
                title="User Guide"
                description="Learn how to make the most of our platform (Coming Soon)"
                showChevron={true}
                onClick={() => {}}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Settings | {getThemes?.name || 'Property Investment Platform'}</title>
        <meta name="description" content="Manage your account preferences and privacy settings" />
      </Helmet>

      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2" style={{ color: getThemes?.txtColor }}>
              Settings
            </h1>
            <p className="text-lg" style={{ color: getThemes?.txtColor }}>
              Manage your account preferences and privacy settings
            </p>
          </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6 hover:shadow-md transition-shadow duration-200">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex px-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 flex-1 text-center ${
                      activeTab === tab.id
                        ? 'rounded-t-lg'
                        : 'border-transparent hover:bg-gray-50 rounded-t-lg'
                    }`}
                    style={{
                      borderBottomColor: activeTab === tab.id ? getThemes?.color1 : 'transparent',
                      color: activeTab === tab.id 
                        ? (getThemes?.txtColor || '#000000')
                        : '#6b7280',
                      backgroundColor: activeTab === tab.id 
                        ? getThemes?.color1 
                        : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.backgroundColor = getThemes?.color2 || '#f3f4f6';
                        e.currentTarget.style.color = getThemes?.txtColor || '#000000';
                        e.currentTarget.style.borderBottomColor = getThemes?.color2 || '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                        e.currentTarget.style.borderBottomColor = 'transparent';
                      }
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 ease-in-out">
          <div className="flex items-center justify-center mb-4">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
              style={{ backgroundColor: getThemes?.color1 }}
            >
              <Zap className="w-5 h-5" style={{ color: getThemes?.txtColor }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Quick Actions
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
            >
              <BarChart3 className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" style={{ color: "black" }} />
              <div>
                <div className="font-medium text-gray-900">View Dashboard</div>
                <div className="text-sm text-gray-500">See your overview and stats</div>
              </div>
            </button>
            
            <button
              onClick={() => navigate("/listing")}
              className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
            >
              <Home className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" style={{ color: "black" }} />
              <div>
                <div className="font-medium text-gray-900">Browse Properties</div>
                <div className="text-sm text-gray-500">Find your next investment</div>
              </div>
            </button>
            
            <button
              onClick={() => navigate("/profile")}
              className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
            >
              <User className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" style={{ color: "black" }} />
              <div>
                <div className="font-medium text-gray-900">Manage Profile</div>
                <div className="text-sm text-gray-500">Update your information</div>
              </div>
            </button>
          </div>
        </div>

        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={() => setModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />

      {/* Terms & Conditions Modal */}
      {isTermsModalOpen && (
        <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
          <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-4xl flex flex-col" style={{ backgroundColor: 'white' }}>
            <div className="flex justify-between items-center p-4 border-b">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold">Terms & Conditions</h3>
                <Link
                  to="/terms-conditions"
                  onClick={() => setIsTermsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                  title="Open full Terms & Conditions page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </Link>
              </div>
              <button
                onClick={() => setIsTermsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              <TermsConditions isModal={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;